import React, { useState } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Spinner,
  Button,
  Tooltip,
  Text,
} from '@fluentui/react-components';
import {
  People24Regular,
  PersonAdd20Regular,
  PlugDisconnected20Regular,
} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { type CustomerFormBag } from './types';
import { removeCustomerCompany, type Customer } from '../../services/customerService';
import { useSectionStyles } from './styles';
import { queryKeys } from '../../lib/queryClient';
import { BaseDialog } from '../BaseDialog';
import CustomerDrawer from '../CustomerDrawer';

interface ContactsSectionProps {
  form: CustomerFormBag;
  /** Hide the title icon when the section is hosted in a context that
   *  already provides framing (e.g. the profile page's card wrapper). */
  showIcon?: boolean;
}

const useStyles = makeStyles({
  contactRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.gap('8px'),
  },
  contactLink: {
    color: tokens.colorBrandForeground1,
    textDecoration: 'none',
    fontWeight: 600,
    cursor: 'pointer',
    alignSelf: 'flex-start',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  // Confirm-dialog body (plain text; the dialog itself carries the warning intent).
  confirmBody: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
  },
});

/**
 * Contacts linked to a Company customer. Each row links to the contact's
 * profile and offers an unlink (confirm-gated) action; an "Add Contact"
 * affordance below opens the create drawer pre-bound to this company so a new
 * contact is created already attached to it (#4). Contacts are create-under-
 * company only — there's no "link an existing record" path, since a contact is
 * definitionally a person scoped to a company (re-parenting is handled by the
 * dedicated reassignment flow). Edit-mode + Company-only.
 */
const ContactsSection: React.FC<ContactsSectionProps> = ({ form, showIcon = true }) => {
  const styles = useSectionStyles();
  const localStyles = useStyles();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    linkedContacts,
    linkedContactsLoading,
    customerId: companyId,
    customerName: companyName,
  } = form;

  const [addOpen, setAddOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Contact pending an unlink confirmation (null = dialog closed).
  const [unlinkTarget, setUnlinkTarget] = useState<Customer | null>(null);

  // Both keys are invalidated: the contacts query that drives this list is
  // keyed ['company-contacts', companyId], and the canonical
  // queryKeys.customers.contacts(companyId) is invalidated too so any other
  // consumer stays fresh. customers.all refreshes the list view (kind/avatars).
  const invalidateContacts = () => {
    if (!companyId) return;
    queryClient.invalidateQueries({ queryKey: ['company-contacts', companyId] });
    queryClient.invalidateQueries({ queryKey: queryKeys.customers.contacts(companyId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
  };

  const handleConfirmUnlink = async () => {
    if (!unlinkTarget || !companyId) return;
    setBusy(true);
    setError(null);
    try {
      // Remove only THIS company's membership — the contact may belong to
      // others (it keeps those links).
      await removeCustomerCompany(unlinkTarget.id, companyId);
      invalidateContacts();
      setUnlinkTarget(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to unlink contact.');
    } finally {
      setBusy(false);
    }
  };

  // A new contact was created (already attached to this company via the
  // drawer's initialLinkedCompanyId binding) — refresh the list and close.
  const handleContactSaved = () => {
    invalidateContacts();
    setAddOpen(false);
  };

  return (
    <>
      <div className={styles.sectionTitle}>{showIcon && <People24Regular />}Contacts</div>
      {linkedContactsLoading ? (
        <Spinner size="small" label="Loading contacts…" />
      ) : linkedContacts.length === 0 ? (
        <div className={styles.emptyStateBlock}>
          No contacts are linked to this company.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {linkedContacts.map((contact: Customer) => (
            <div key={contact.id} className={localStyles.contactRow}>
              <a
                href={`/customers/${contact.id}`}
                className={localStyles.contactLink}
                onClick={(e) => {
                  // Let modifier-clicks fall through to the browser so
                  // middle/ctrl/cmd-click open the contact in a new tab.
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                  e.preventDefault();
                  navigate(`/customers/${contact.id}`);
                }}
              >
                {contact.name}
              </a>
              <Tooltip content="Unlink from company" relationship="label">
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<PlugDisconnected20Regular />}
                  aria-label={`Unlink ${contact.name} from company`}
                  disabled={busy || !companyId}
                  onClick={() => setUnlinkTarget(contact)}
                />
              </Tooltip>
            </div>
          ))}
        </div>
      )}

      {error && (
        <Text size={200} style={{ color: tokens.colorPaletteRedForeground1, marginTop: '6px' }}>
          {error}
        </Text>
      )}

      {/* Add a new contact, created pre-attached to this company. Disabled until
          the company is saved (needs an id to bind the contact to). */}
      <Button
        appearance="subtle"
        size="small"
        icon={<PersonAdd20Regular />}
        disabled={!companyId || busy}
        style={{ alignSelf: 'flex-start', marginTop: '8px' }}
        onClick={() => { setError(null); setAddOpen(true); }}
      >
        Add Contact
      </Button>

      {addOpen && (
        <CustomerDrawer
          isOpen={addOpen}
          onClose={() => setAddOpen(false)}
          initialKind="contact"
          initialLinkedCompanyId={companyId}
          initialLinkedCompanyName={companyName}
          onSaved={handleContactSaved}
        />
      )}

      <BaseDialog
        isOpen={unlinkTarget !== null}
        onClose={() => { if (!busy) setUnlinkTarget(null); }}
        title="Unlink contact"
        maxWidth="420px"
        actions={
          <>
            <Button appearance="secondary" disabled={busy} onClick={() => setUnlinkTarget(null)}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={busy} onClick={handleConfirmUnlink}>
              {busy ? 'Unlinking…' : 'Unlink'}
            </Button>
          </>
        }
      >
        <div className={localStyles.confirmBody}>
          <Text>
            Remove <strong>{unlinkTarget?.name}</strong> from this company? The contact
            record stays, but it will no longer be associated with the company.
          </Text>
        </div>
      </BaseDialog>
    </>
  );
};

export default ContactsSection;
